import React, { useEffect, useRef, useState } from "react";
import { observer } from "mobx-react-lite";
import { Alert, Button, Card, Drawer, Empty, Spin, Typography } from "antd";
import { CloseOutlined, FileTextOutlined } from "@ant-design/icons";
import { useWorkbenchStore } from "../../workbench/stores/context";
import { WbActivity, WbDocument, WbTask } from "../../workbench/types/WbTypes";
import WorkbenchNewDocumentPreviewDrawer from "./WorkbenchNewDocumentPreviewDrawer";
import WorkbenchNewTaskDrawer from "./WorkbenchNewTaskDrawer";
import {
    formatActivityDate,
    formatActivityClock,
    formatActivityTime,
    getSubjectTitle,
} from "../utils/workbenchNewSubjectView";

export const groupActivityByDate = (events: WbActivity[]) => {
    const groups: { date: string; label: string; events: WbActivity[] }[] = [];
    for (const event of events) {
        const time = new Date(event.occurredAt);
        const date = `${time.getFullYear()}-${time.getMonth()}-${time.getDate()}`;
        const group = groups[groups.length - 1];
        if (group?.date === date) group.events.push(event);
        else
            groups.push({
                date,
                label: formatActivityDate(event.occurredAt),
                events: [event],
            });
    }
    return groups;
};

const WorkbenchNewActivity: React.FC<{ subjectId: string }> = ({
    subjectId,
}) => {
    const { workbenchStore: store } = useWorkbenchStore();
    const [open, setOpen] = useState(false);
    const [document, setDocument] = useState<WbDocument | null>(null);
    const [task, setTask] = useState<WbTask | null>(null);
    const trigger = useRef<React.ElementRef<typeof Button>>(null);
    const closeButton = useRef<React.ElementRef<typeof Button>>(null);
    const previewTrigger = useRef<HTMLElement | null>(null);
    useEffect(() => {
        setOpen(false);
        setDocument(null);
        setTask(null);
        previewTrigger.current = null;
    }, [subjectId]);
    const refresh = () => {
        if (store.subjectData?.id === subjectId) {
            void store.loadActivityContent(store.subjectData);
            void store.loadDocumentsContent(store.subjectData);
            void store.loadTasksContent(store.subjectData);
        }
    };
    const destination = (event: WbActivity) => {
        if (!event.canOpen) return null;
        if (event.kind === "DocumentUploaded")
            return (
                store.documentsData.find(
                    (d) => d.packageId === event.packageId,
                ) ?? null
            );
        if (
            ["TaskCreated", "TaskUpdated", "TaskCompleted"].includes(event.kind)
        )
            return store.tasksData.find((t) => t.id === event.taskId) ?? null;
        return event.sessionUrl ?? null;
    };
    const activate = (event: WbActivity, element: HTMLElement) => {
        const target = destination(event);
        if (!target) return;
        if (typeof target === "string")
            window.open(target, "_blank", "noopener,noreferrer");
        else {
            previewTrigger.current = element;
            if ("packageId" in target) setDocument(target);
            else setTask(target);
        }
    };
    const row = (event: WbActivity, full: boolean) => {
        const content = (
            <span className="workbench-contract-activity-content">
                <span className="workbench-new-subject-activity-icon">
                    <FileTextOutlined />
                </span>
                <span className="workbench-new-subject-activity-copy">
                    <span className="workbench-new-subject-activity-title">
                        {event.label}
                    </span>
                    <span className="workbench-new-subject-activity-detail">
                        {event.detail}
                    </span>
                </span>
                <time
                    className="workbench-new-subject-activity-meta"
                    dateTime={event.occurredAt}
                >
                    {full
                        ? formatActivityClock(event.occurredAt)
                        : formatActivityTime(event.occurredAt)}
                </time>
            </span>
        );
        const className = `workbench-new-subject-activity-row workbench-contract-activity-row${full ? " workbench-contract-activity-full" : ""}`;
        return destination(event) ? (
            <Button
                key={event.id}
                type="text"
                className={className}
                onClick={(click) => activate(event, click.currentTarget)}
            >
                {content}
            </Button>
        ) : (
            <div key={event.id} className={className}>
                {content}
            </div>
        );
    };
    const error = store.activityWidget.error;
    const status = (
        <>
            {error && (
                <Alert
                    type="warning"
                    message={error}
                    action={<Button onClick={refresh}>Retry</Button>}
                />
            )}
            {store.activityWidget.isLoading && <Spin size="small" />}
        </>
    );
    return (
        <>
            <Card className="workbench-new-subject-card workbench-new-subject-activity-card">
                <div className="workbench-new-subject-card-header-row">
                    <div className="workbench-new-subject-card-title workbench-new-subject-card-title-small">
                        Latest activity
                    </div>
                    <Button
                        ref={trigger}
                        type="link"
                        onClick={() => {
                            setOpen(true);
                            refresh();
                        }}
                    >
                        Show all
                    </Button>
                </div>
                <div className="workbench-contract-activity-compact">
                    {error && (
                        <Alert
                            type="warning"
                            message={error}
                            action={<Button onClick={refresh}>Retry</Button>}
                        />
                    )}
                    <Spin spinning={store.activityWidget.isLoading}>
                        {!store.activityWidget.isLoading &&
                            !error &&
                            store.activityData.length === 0 && (
                                <Empty
                                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                                    description="No activity yet"
                                />
                            )}
                        {!error &&
                            store.activityData
                                .slice(0, 3)
                                .map((event) => row(event, false))}
                    </Spin>
                </div>
            </Card>
            <Drawer
                aria-modal={false}
                className="workbench-task-drawer workbench-contract-activity-drawer"
                closable={false}
                title={
                    <div className="workbench-task-drawer-heading">
                        <div className="workbench-task-drawer-title-copy">
                            <Typography.Title
                                className="workbench-task-drawer-title"
                                level={2}
                            >
                                Activity
                            </Typography.Title>
                            <div className="workbench-task-drawer-subtitle">
                                {getSubjectTitle(store.subjectData)}
                            </div>
                        </div>
                    </div>
                }
                extra={
                    <Button
                        aria-label="Close activity"
                        ref={closeButton}
                        icon={<CloseOutlined />}
                        onClick={() => setOpen(false)}
                    />
                }
                placement="right"
                width={640}
                open={open}
                keyboard={!task && !document}
                mask={false}
                onClose={() => setOpen(false)}
                afterOpenChange={(visible) => {
                    if (visible) closeButton.current?.focus();
                    else trigger.current?.focus();
                }}
            >
                <div className="workbench-contract-activity-drawer-body">
                    {status}
                    {!store.activityWidget.isLoading &&
                        !error &&
                        store.activityData.length === 0 && (
                            <Empty description="No activity yet" />
                        )}
                    {groupActivityByDate(store.activityData).map((group) => (
                        <section key={group.date}>
                            <h3>{group.label}</h3>
                            {group.events.map((event) => row(event, true))}
                        </section>
                    ))}
                </div>
            </Drawer>
            {document && (
                <WorkbenchNewDocumentPreviewDrawer
                    document={document}
                    onDownload={store.handleAttachmentDownload}
                    onClose={() => {
                        setDocument(null);
                        previewTrigger.current?.focus();
                        refresh();
                    }}
                />
            )}
            <WorkbenchNewTaskDrawer
                task={task}
                onClose={() => {
                    setTask(null);
                    previewTrigger.current?.focus();
                    refresh();
                }}
            />
        </>
    );
};

export default observer(WorkbenchNewActivity);
